import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { fabric } from 'fabric';
import {
  CUIText,
  Dialog,
  DialogContentType,
  DialogFooterType,
  LoadingIndicator,
  ToastVariants,
} from '@dotcom/crown-ui';
import { thumbnailWidth } from '../../../constants';
import {
  addPhotoTrayImages,
  setAllDrawersClosed,
  setPhotoDrawerClosed,
  setIsToasterOpen,
  useAppContext,
} from '../../../context/app-context';
import { useUIStore } from '../../../stores/ui';
import { useCardContext } from '../../../context/card-context/card-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { usePhoto } from '../../../features/card-editor/hooks/use-photo';
import { useHistoryTransaction } from '../../../features/edit-history/edit-history';
import { ImageResponse } from '../../../global-types';
import { PhotoTrayCustomFormats } from '../../../global-types/images';
import {
  useActiveCanvas,
  useDeletePhotoTrayImages,
  useFeatureFlags,
  usePhotoDrawerInfiniteScroll,
} from '../../../hooks';
import { useSaveProject } from '../../../hooks/use-save-project';
import { linkPhoto } from '../../../services';
import { CanvasDataTypes } from '../../../utils';
import { loadImageWithRetry } from '../../../utils/canvas-utils/load-image-with-retry';
import { DeleteImagesConfirmationDialog } from '../../delete-images-dialog/delete-images-confirmation-dialog';
import { ReplaceImageProps } from '../image-edit-drawer/image-edit-drawer-types';
import { useToolbarPosition } from '../toolbar-action-menu';
import { NoPhotosUploaded } from './fragments/no-photos-uploaded';
import { getPhotoDrawerHost, setPhotoDrawerCloseHandler } from './photo-drawer-host';
import { PhotosUploaded } from './fragments/photos-uploaded';
import styles from './photo-drawer.module.scss';

const normalizeUploadError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  return new Error('Error uploading image');
};

export const PhotoDrawer = () => {
  const { appState, appDispatch } = useAppContext();
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const { t } = useTranslation();
  const { IS_DRAWER_PAGINATION_ENABLED, IS_PHOTO_DRAWER_REDESIGN_ENABLED } = useFeatureFlags();
  const closeTextDrawer = useUIStore((state) => state.closeTextDrawer);
  const closeAllDrawers = useCallback(() => {
    setAllDrawersClosed(appDispatch);
    setPhotoDrawerClosed(appDispatch);
    closeTextDrawer();
  }, [appDispatch, closeTextDrawer]);
  const { isImageUploadToPhotoZoneOpen, isImageUploadDrawerOpen, photoTrayImagesPage, isMobileApp } = appState;
  const [selectedPhotos, setSelectedPhotos] = useState<ImageResponse[]>([]);
  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const customFormats = PhotoTrayCustomFormats;
  const isAddingToCanvas = useRef(false);
  const canvas = useActiveCanvas();
  const { cardState } = useCardContext();
  const { cardFacesList, activeCardIndex } = cardState;
  const currentCardFace = cardFacesList[`${activeCardIndex}`];
  const isFrontFace = currentCardFace?.type === 'front';

  const isOpen = isImageUploadDrawerOpen || isImageUploadToPhotoZoneOpen;

  // Align photo drawer with the canvas wrapper top edge (same as other toolbars)
  useToolbarPosition({
    getHost: getPhotoDrawerHost,
    getAnchor: () => canvas?.current?.getElement()?.closest<HTMLElement>('[data-testid^="canvas-wrapper-"]') ?? null,
    enabled: !isMobileApp && IS_PHOTO_DRAWER_REDESIGN_ENABLED && isOpen,
    measurementKey: activeCardIndex,
  });

  const [hasMoreImages, setHasMoreImages] = useState(true);
  const observerTarget = useRef(null);
  const footerObserverTarget = <div ref={observerTarget} className={styles['footer-observer-target']}></div>;
  const { addPhotoToCardFace, addPhotoToPhotozone, fillPhotoZone } = usePhoto();
  const { saveProject } = useSaveProject();
  const runHistoryTransaction = useHistoryTransaction();
  const replaceImagePhotoZoneId = useRef('');
  const desktopPanelRef = useRef<HTMLDivElement>(null);
  const {
    initializedDataState: { data: initializedData, isUK },
  } = useInitializationDataContext();

  const {
    isUploading,
    isFetching,
    onUploadImages,
    photoTrayImagesFinal,
    setPhotoTrayImages,
    handleDeleteImageLocally,
  } = usePhotoDrawerInfiniteScroll(
    appDispatch,
    isImageUploadDrawerOpen,
    isImageUploadToPhotoZoneOpen,
    setHasMoreImages,
    hasMoreImages,
    IS_DRAWER_PAGINATION_ENABLED,
    photoTrayImagesPage,
    observerTarget,
  );

  useEffect(() => {
    if (isImageUploadDrawerOpen || isImageUploadToPhotoZoneOpen) {
      isAddingToCanvas.current = false;
      setHasMoreImages(true);
      addPhotoTrayImages(appDispatch, []);
    }
  }, [isImageUploadDrawerOpen, isImageUploadToPhotoZoneOpen, appDispatch, setHasMoreImages]);

  useEffect(() => {
    setHasMoreImages(false);
  }, [closeAllDrawers]);

  const {
    setImagesToDelete,
    deleteImagesConfirmationOpen,
    handleDeleteImagesConfirmation,
    handleOpenDeleteImagesConfirmationDialog,
    isDeleting,
  } = useDeletePhotoTrayImages(setPhotoTrayImages, handleDeleteImageLocally, setIsDeleteMode);

  const onDeleteImages = (photosToDelete: ImageResponse[]) => {
    handleOpenDeleteImagesConfirmationDialog(true);
    setImagesToDelete(photosToDelete);
  };

  const ConfirmDeleteDialog = {
    dialogTitle: t('photoDrawer.deleteDialog.dialogTitle'),
    dialogContent: t('photoDrawer.deleteDialog.dialogContent'),
    confirmButtonText: t('photoDrawer.deleteDialog.actionButton'),
    cancelButtonText: t('photoDrawer.deleteDialog.cancelButton'),
  };

  const NoPhotosUploadedLabels = {
    title: t('photoDrawer.noPhotosUploaded.title'),
    steps: t('photoDrawer.noPhotosUploaded.steps', { returnObjects: true }) as string[],
  };

  const PhotosUploadedLabels = {
    uploadPhoto: t('photoDrawer.photosUploaded.uploadPhoto'),
    deleteImage: t('photoDrawer.photosUploaded.deleteImage'),
    cancel: t('photoDrawer.photosUploaded.cancel'),
  };
  const photoDrawerTitle = t('photoDrawer.title') as string;

  const onUploadError = useCallback(
    (error: Error) => {
      setIsToasterOpen(appDispatch, {
        title: 'Error uploading image',
        children: error.message,
        variant: ToastVariants.Error,
      });
    },
    [appDispatch],
  );

  const onUploadImageChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      try {
        await onUploadImages(event);
      } catch (error) {
        const uploadError = normalizeUploadError(error);
        onUploadError(uploadError);
        // Intentionally do not rethrow from an input change handler. We surface the
        // error through toast UI and avoid unhandled Promise rejections in React events.
      } finally {
        // Allow selecting the same file again after any upload attempt.
        event.target.value = '';
      }
    },
    [onUploadImages, onUploadError],
  );

  // Reset replace flag when drawer closes so it doesn't stick
  useEffect(() => {
    if (!isOpen) {
      const activeObj = canvas?.current?.getActiveObject();
      if (activeObj) {
        (activeObj as ReplaceImageProps).isReplacingUserImage = false;
      }
    }
  }, [isOpen, canvas]);

  const onPhotoSelect = async (photo: ImageResponse) => {
    try {
      if (isAddingToCanvas.current) {
        return;
      }
      isAddingToCanvas.current = true;

      // Capture active object at the moment of interaction, not render time
      const activeObject = canvas?.current?.getActiveObject();
      replaceImagePhotoZoneId.current = activeObject?.data?.photoZoneId || '';

      const data = {
        image_reference_id: photo.image_reference_id || photo.photo_tray_image_id,
      };

      const response = await linkPhoto(data, initializedData?.project_id as string);

      // Replace existing image on canvas
      if (activeObject && (activeObject as ReplaceImageProps).isReplacingUserImage && response.data) {
        const imageUrl = response.data.image_url;
        const imageId = response.data.image_id;
        const replaceImage = () =>
          new Promise<void>((resolve, reject) => {
            (activeObject as fabric.Image).setSrc(
              imageUrl,
              (_image: fabric.Image, isError?: boolean) => {
                if (isError) {
                  reject(new Error('Error loading replacement image'));
                  return;
                }
                const lowResIcon = canvas?.current
                  ?.getObjects?.()
                  ?.find(
                    (obj) => obj.data?.type === CanvasDataTypes.PhotoIcon && obj.data?.parentName === activeObject.name,
                  );
                if (lowResIcon) {
                  canvas?.current?.remove(lowResIcon);
                }
                canvas?.current?.remove(activeObject);
                activeObject.set({
                  name: imageId,
                  data: {
                    type: CanvasDataTypes.UserImage,
                  },
                  borderColor: isUK ? '#333333' : '#604099',
                });
                if (replaceImagePhotoZoneId.current !== '') {
                  fillPhotoZone({ image: activeObject as fabric.Image, photozoneId: replaceImagePhotoZoneId.current });
                }
                canvas?.current?.renderAll();
                resolve();
              },
              { crossOrigin: 'anonymous' },
            );
          });
        const targetCanvas = canvas?.current;
        await (targetCanvas ? runHistoryTransaction(targetCanvas, replaceImage) : replaceImage());
        saveProject({ showLoadingScreen: false, shouldRestoreCanvas: false, generateFrontPreview: false });
        isAddingToCanvas.current = false;
        // The Photo Drawer stays open after a replace, so the replace flag must
        // NOT be reset here. Resetting it turned every subsequent thumbnail
        // click into a brand-new "add" (falling to the ELSE branch:
        // addPhotoToPhotozone, which fills the next empty zone) instead of
        // replacing the same zone again. Keeping the flag true lets the user
        // replace the same image repeatedly; the "!isOpen" effect above still
        // clears it when the drawer closes so it doesn't stick.
      } else {
        // Add new image to canvas
        const urlObj = new URL(response.data?.image_url || '');
        urlObj.searchParams.delete('w');
        const imageElement = await loadImageWithRetry(urlObj.toString());

        const imgInstance = new fabric.Image(imageElement, {
          top: 100,
          left: 100,
          name: response.data?.image_id,
          data: {
            ...data,
            type: CanvasDataTypes.UserImage,
          },
          borderColor: isUK ? '#333333' : '#604099',
        });

        if (!imgInstance.width || !imgInstance.height) {
          throw new Error('Image has zero dimensions after loading.');
        }

        if (isFrontFace) {
          await addPhotoToPhotozone(imgInstance);
        } else {
          await addPhotoToCardFace(imgInstance);
        }
        isAddingToCanvas.current = false;
      }
    } catch (error) {
      isAddingToCanvas.current = false;
      onUploadError(normalizeUploadError(error));
    }
  };

  const onCancelDelete = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsDeleteMode(false);
    setSelectedPhotos([]);
  };

  const handleDeleteImages = (photos: ImageResponse[], e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteImages(photos);
  };

  const handleClosingDrawer = useCallback(() => {
    onCancelDelete();
    closeAllDrawers();
  }, [closeAllDrawers]);

  const isMobileBottomSheetEnabled = isMobileApp && IS_PHOTO_DRAWER_REDESIGN_ENABLED;
  const isDesktopRedesignEnabled = !isMobileApp && IS_PHOTO_DRAWER_REDESIGN_ENABLED;

  const beginCloseDrawer = useCallback(() => {
    // The crown-ui Dialog calls onClose() in its useEffect on mount (isOpen=false).
    // Guard here so that phantom close calls never start the close animation.
    if (!isOpen) return;
    if (isClosing) {
      return;
    }

    setIsClosing(true);
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = window.setTimeout(() => {
      setIsClosing(false);
      handleClosingDrawer();
    }, 180);
  }, [handleClosingDrawer, isClosing, isOpen]);

  useEffect(() => {
    if (isImageUploadDrawerOpen || isImageUploadToPhotoZoneOpen) {
      setIsClosing(false);
    }
  }, [isImageUploadDrawerOpen, isImageUploadToPhotoZoneOpen]);

  // Register the animated close handler so the editor's single click-outside
  // dispatcher can dismiss this drawer (with its slide-out animation) when it
  // is the top-most open drawer. Clicks on the canvas are handled separately by
  // use-canvas-mouse-up; everything else (header, page chrome) flows through
  // the editor dispatcher.
  useEffect(() => {
    setPhotoDrawerCloseHandler(beginCloseDrawer);
    return () => setPhotoDrawerCloseHandler(null);
  }, [beginCloseDrawer]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleDragGestureStart = (clientY: number) => {
    if (!isMobileBottomSheetEnabled) {
      return;
    }

    setDragStartY(clientY);
    setDragOffsetY(0);
  };

  const handleDragGestureMove = (clientY: number) => {
    if (dragStartY === null) {
      return;
    }

    const nextOffset = Math.max(0, clientY - dragStartY);
    setDragOffsetY(nextOffset);
  };

  const handleDragGestureEnd = () => {
    if (dragStartY === null) {
      return;
    }

    const shouldCloseFromGesture = dragOffsetY > 120;
    setDragStartY(null);
    setDragOffsetY(0);

    if (shouldCloseFromGesture) {
      beginCloseDrawer();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (inputFileRef.current && event.dataTransfer.files.length) {
      inputFileRef.current.files = event.dataTransfer.files;
      const changeEvent = new Event('change', { bubbles: true });
      inputFileRef.current.dispatchEvent(changeEvent);
    }
  };

  const updatedPhotoTrayImages = photoTrayImagesFinal.map((image) => ({
    ...image,
    thumbnail_url: `${image.thumbnail_url}?${thumbnailWidth}`,
  }));
  const isDialogOpen = isOpen || isClosing;
  const shouldWaitForInitialData =
    isDialogOpen && updatedPhotoTrayImages.length < 1 && !isFetching && !isUploading && hasMoreImages;
  const isLoading =
    ((isFetching || isUploading) && photoTrayImagesFinal.length < 1) || isDeleting || shouldWaitForInitialData;
  const showPhotosUploaded = updatedPhotoTrayImages !== undefined && updatedPhotoTrayImages.length > 0;

  const dialogClasses = classNames(
    styles['photo-drawer'],
    styles['photo-drawer-dialog'],
    isDesktopRedesignEnabled && styles['photo-drawer-desktop-redesign'],
    isMobileBottomSheetEnabled && styles['photo-drawer-mobile-redesign'],
    isClosing && styles['photo-drawer-closing'],
    isMobileBottomSheetEnabled && dragOffsetY > 0 && styles['photo-drawer-dragging'],
  );

  const dialogWrapperClasses = classNames(styles['photo-drawer-wrapper']);

  const dragOffsetStyle =
    isMobileBottomSheetEnabled && dragOffsetY > 0
      ? ({ '--drag-offset': `${dragOffsetY}px` } as React.CSSProperties)
      : undefined;

  const drawerContent = (
    // dragOffsetStyle sets the --drag-offset CSS custom property consumed by
    // the .drawer-content CSS rule (transform: translateY(var(--drag-offset, 0))).
    // Inline style is required to dynamically update the CSS variable during drag gestures.
    <div className={styles['drawer-content']} onDrop={handleDrop} onDragOver={handleDragOver} style={dragOffsetStyle}>
      {!isLoading && (
        <>
          {/* Crown UI Input does not support type="file". This hidden input is
              triggered programmatically via inputRef.current?.click() and is never
              rendered visually (file-input-hidden class applies display: none). */}
          <input
            type="file"
            ref={inputFileRef}
            onChange={onUploadImageChange}
            className={styles['file-input-hidden']}
            accept={customFormats}
            multiple
            data-testid={'photo-drawer-input'}
          />
          {showPhotosUploaded ? (
            <PhotosUploaded
              labels={PhotosUploadedLabels}
              photoTrayImages={updatedPhotoTrayImages}
              onDeleteImages={(photos, e) => handleDeleteImages(photos as ImageResponse[], e as React.MouseEvent)}
              onUploadImages={() => inputFileRef.current?.click()}
              selectedPhotos={selectedPhotos}
              setSelectedPhotos={setSelectedPhotos}
              isDeleting={isDeleteMode}
              setIsDeleting={setIsDeleteMode}
              onCancelDelete={onCancelDelete}
              onPhotoSelect={onPhotoSelect}
              isUploading={isUploading}
              footerContent={footerObserverTarget}
              isFooterSticky={true}
            />
          ) : (
            <NoPhotosUploaded labels={NoPhotosUploadedLabels} inputFileRef={inputFileRef} />
          )}
        </>
      )}
      {isLoading && <LoadingIndicator />}
    </div>
  );

  const photoDrawerHeader = (
    <>
      <CUIText variant="h4" as="h2" className={styles['photo-drawer-title']}>
        {photoDrawerTitle}
      </CUIText>
      {isMobileBottomSheetEnabled && (
        <div
          className={styles['drawer-drag-handle-zone']}
          onPointerDown={(event) => handleDragGestureStart(event.clientY)}
          onPointerMove={(event) => handleDragGestureMove(event.clientY)}
          onPointerUp={handleDragGestureEnd}
          onPointerCancel={handleDragGestureEnd}
        >
          <div className={styles['drawer-drag-handle']} />
        </div>
      )}
    </>
  );

  const desktopPanelClasses = classNames(
    styles['photo-drawer-desktop-panel'],
    isClosing && styles['photo-drawer-closing'],
  );

  const host = getPhotoDrawerHost();

  // Desktop redesign: portal directly into the host element (same pattern
  // as FontToolbar / ImageEditToolbar) so the panel sits beside the card.
  // Crown UI Dialog always portals to document.body which breaks this layout.
  const desktopPanel =
    isDesktopRedesignEnabled && isDialogOpen && host
      ? createPortal(
          <div className={desktopPanelClasses} data-testid="photo-drawer" ref={desktopPanelRef}>
            <button
              id="photo-drawer-close-button"
              className={styles['photo-drawer-close-button']}
              onClick={beginCloseDrawer}
              aria-label="Close"
              data-testid="photo-drawer-close-button"
            >
              ✕
            </button>
            {photoDrawerHeader}
            {drawerContent}
          </div>,
          host,
        )
      : null;

  // Mobile / non-redesign: use Crown UI Dialog (portals to body, handles
  // overlay, focus trap, etc.)
  const dialogPanel =
    !isDesktopRedesignEnabled && isDialogOpen ? (
      <Dialog
        id="photo-drawer-dialog"
        testId="photo-drawer"
        titleDisabled
        type={DialogContentType.Modal}
        isOpen={isDialogOpen}
        onClose={beginCloseDrawer}
        footerType={DialogFooterType.Passive}
        dialogClassName={dialogClasses}
        dialogWrapperClassName={dialogWrapperClasses}
        closeButtonId="photo-drawer-close-button"
        hideOverlay={true}
        disableDialogScroll={true}
      >
        {photoDrawerHeader}
        {drawerContent}
      </Dialog>
    ) : null;

  return (
    <>
      {desktopPanel}
      {dialogPanel}
      <div ref={observerTarget}></div>
      <DeleteImagesConfirmationDialog
        isOpen={deleteImagesConfirmationOpen}
        onClose={() => handleOpenDeleteImagesConfirmationDialog(false)}
        onConfirm={handleDeleteImagesConfirmation}
        domId="delete-confirmation-dialog"
        contentTitle={ConfirmDeleteDialog.dialogTitle}
        contentText={ConfirmDeleteDialog.dialogContent}
        actionButtonText={ConfirmDeleteDialog.confirmButtonText}
        cancelButtonText={ConfirmDeleteDialog.cancelButtonText}
      />
    </>
  );
};
